import type { ReactElement, ReactNode } from 'react';

import { t } from '@/shared/i18n';

import styles from './toast.module.css';

export interface ToastProps {
  /** Сообщение. Одна строка текста, при нехватке места переносится как текст. */
  children: ReactNode;
  /**
   * Кнопки действий — СВОЕЙ строкой под сообщением.
   *
   * Отдельным слотом, потому что вперемешку с текстом они ломали вёрстку: `.body` был
   * flex-контейнером с переносом, кнопки уезжали на вторую строку каждая, тост вырастал
   * втрое, а крестик, выровненный по центру, оказывался в его середине посреди пустоты.
   * Текст и тап-цели ≥48px не живут в одном потоке.
   */
  actions?: ReactNode;
  /**
   * Раскрываемое содержимое ПОД строкой сообщения, внутри самого тоста.
   *
   * Отдельным слотом, а не частью `children`, потому что у него другое поведение:
   * строка сообщения короткая и всегда видна целиком, а это может быть список из
   * десяти этикеток — ему нужна своя прокрутка и своя граница высоты, иначе тост
   * накроет экран. Первая версия рендерила такой список ПОСЛЕ тоста, в потоке
   * документа: сообщение всплывало сверху, а его содержимое оказывалось в конце
   * страницы, до которого надо было доскроллить.
   */
  extra?: ReactNode;
  /**
   * Закрыть руками. Без него тост живёт до своего таймера — а таймер здесь до пяти
   * минут, и всё это время он перекрывает содержимое под собой.
   */
  onClose?: () => void;
  /**
   * `status` — обычное сообщение о результате, `alert` — то, что нельзя пропустить.
   *
   * Для наших двух случаев верен `status`: и печать, и списание УЖЕ произошли,
   * человек их сам вызвал, и `alert` перебивал бы скринридер на каждой этикетке.
   */
  role?: 'status' | 'alert';
}

/**
 * Всплывающее сообщение о результате действия (T-019).
 *
 * Зачем существует. И «Напечатано», и «Списано, отменить» раньше рисовались ВНУТРИ
 * потока документа, в самом низу экрана — после списка заготовок. На моноблоке список
 * длинный, и человек, напечатавший этикетку по позиции из начала списка, сообщения
 * просто не видел: оно оказывалось за пределами вьюпорта. Вместе с сообщением он не
 * видел и кнопку аннулирования — то есть единственный способ отменить ошибку был
 * доступен только тому, кто печатал последнюю позицию в списке.
 *
 * Обратимость печати — не удобство, а замена окну подтверждения (`DESIGN.md`: «окна
 * подтверждения нет, превью — результат»). Если кнопка отмены не видна, подтверждения
 * нет ни в каком виде.
 *
 * Почему СВЕРХУ, а не снизу. Первая версия всплывала снизу по центру — «взгляд после
 * тапа там же, где кнопка». Аргумент оказался ровно причиной дефекта: снизу живёт
 * липкий футер с основной кнопкой печати, и тост её перекрывал. Повар, напечатавший
 * одну этикетку, не мог напечатать вторую, пока тост не исчезнет сам. Внизу у экрана
 * печати места нет ни при какой вёрстке — значит верх.
 *
 * Почему без анимации. `DESIGN.md`: на плотности `kiosk` смены состояний мгновенные,
 * 0ms, «никакой хореографии». Выезжающий тост здесь был бы задержкой между действием
 * и подтверждением — как раз там, где её быть не должно.
 */
export function Toast({
  children,
  actions,
  extra,
  onClose,
  role = 'status',
}: ToastProps): ReactElement {
  return (
    <div className={styles.layer} role={role}>
      <div className={styles.toast}>
        <div className={styles.head}>
          <p className={styles.body}>{children}</p>

          {onClose ? (
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label={t('common.close')}
            >
              ×
            </button>
          ) : null}
        </div>

        {actions ? <div className={styles.actions}>{actions}</div> : null}
        {extra ? <div className={styles.extra}>{extra}</div> : null}
      </div>
    </div>
  );
}
